import { execFile } from 'node:child_process';
import { createHash, randomBytes } from 'node:crypto';
import { mkdir, readFile, rm, writeFile } from 'node:fs/promises';
import path from 'node:path';
import { promisify } from 'node:util';
import type { PrismaClient } from '@prisma/client';
import express, { Router, type Request } from 'express';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp } from '../../core/http.js';
import { authenticate, permissionsGranted } from '../auth/auth.middleware.js';

const exec = promisify(execFile);
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

interface Deps {
  db: PrismaClient;
  config: Config;
  /** Where files are stored: outside the web root, only served through authorised endpoints. */
  storageDir?: string;
}

/** Later phases register invoices, bills and so on here. */
interface EntityPolicy {
  view: (req: Request) => boolean;
  edit: (req: Request) => boolean;
  exists: (db: PrismaClient, companyId: number, id: number, req: Request) => Promise<boolean>;
}
const can = (req: Request, p: string) => permissionsGranted(req.auth!.permissions, p);
const policies: Record<string, EntityPolicy> = {
  party: {
    view: (r) => can(r, 'sales.client.view') || can(r, 'purchases.vendor.view'),
    edit: (r) => can(r, 'sales.client.edit') || can(r, 'purchases.vendor.edit'),
    exists: async (db, companyId, id) => !!(await db.party.findFirst({ where: { id, companyId } })),
  },
  item: {
    view: (r) => can(r, 'sales.item.view'),
    edit: (r) => can(r, 'sales.item.edit'),
    exists: async (db, companyId, id) => !!(await db.item.findFirst({ where: { id, companyId } })),
  },
};
export const registerAttachmentPolicy = (entityType: string, policy: EntityPolicy) => {
  policies[entityType] = policy;
};

interface Sniffed {
  mime: string;
  ext: string[];
}

/** Identify the real type from the file's first bytes; the client-declared type is never trusted. */
export function sniff(buf: Buffer): Sniffed | null {
  if (buf.subarray(0, 4).toString('latin1') === '%PDF')
    return { mime: 'application/pdf', ext: ['.pdf'] };
  if (buf.subarray(0, 8).equals(Buffer.from([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])))
    return { mime: 'image/png', ext: ['.png'] };
  if (buf[0] === 0xff && buf[1] === 0xd8 && buf[2] === 0xff)
    return { mime: 'image/jpeg', ext: ['.jpg', '.jpeg'] };
  if (
    buf.subarray(0, 4).toString('latin1') === 'RIFF' &&
    buf.subarray(8, 12).toString('latin1') === 'WEBP'
  )
    return { mime: 'image/webp', ext: ['.webp'] };
  if (buf[0] === 0x50 && buf[1] === 0x4b && buf[2] === 0x03 && buf[3] === 0x04) {
    return { mime: 'application/vnd.openxmlformats-officedocument', ext: ['.docx', '.xlsx'] };
  }
  return null;
}

const UNSAFE_NAME_CHARS = new Set(['<', '>', ':', '"', '/', '\\', '|', '?', '*']);

/** Strips path parts and control/reserved characters so a name can never escape its folder. */
export const safeFilename = (name: string) =>
  Array.from(path.basename(name))
    .map((ch) => (ch.charCodeAt(0) < 32 || UNSAFE_NAME_CHARS.has(ch) ? '_' : ch))
    .join('')
    .replace(/^\.+/, '')
    .slice(0, 200) || 'file';

/** Optional antivirus hook: set AV_SCAN_COMMAND (e.g. "clamscan --no-summary"); a non-zero exit rejects the upload. */
async function scan(file: string) {
  const cmd = process.env.AV_SCAN_COMMAND;
  if (!cmd) return;
  const [bin, ...args] = cmd.split(' ');
  try {
    await exec(bin!, [...args, file]);
  } catch {
    throw new HttpError(422, 'FILE_REJECTED', 'The file was rejected by the virus scanner');
  }
}

export function createAttachmentsRouter({ db, config, storageDir }: Deps) {
  const router = Router();
  const root = storageDir ?? path.resolve(process.cwd(), 'storage', 'attachments');
  router.use('/attachments', authenticate({ db, config }));
  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });

  function policyFor(req: Request, entityType: string) {
    const p = policies[entityType];
    if (!p)
      throw new HttpError(
        400,
        'UNSUPPORTED_ENTITY',
        'Files cannot be attached to this record type',
      );
    return p;
  }

  router.get(
    '/attachments',
    asyncHandler(async (req, res) => {
      const entityType = String(req.query.entityType ?? '');
      const entityId = Number(req.query.entityId);
      const policy = policyFor(req, entityType);
      if (!policy.view(req) || !(await policy.exists(db, req.auth!.companyId, entityId, req)))
        throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
      const rows = await db.attachment.findMany({
        where: { companyId: req.auth!.companyId, entityType, entityId, deletedAt: null },
        orderBy: { id: 'desc' },
      });
      res.json({
        items: rows.map((a) => ({
          id: a.id,
          filename: a.filename,
          mimeType: a.mimeType,
          sizeBytes: a.sizeBytes,
          createdAt: a.createdAt,
        })),
      });
    }),
  );

  router.post(
    '/attachments',
    express.raw({ type: () => true, limit: MAX_ATTACHMENT_BYTES + 1024 }),
    asyncHandler(async (req, res) => {
      const entityType = String(req.query.entityType ?? '');
      const entityId = Number(req.query.entityId);
      const policy = policyFor(req, entityType);
      if (!policy.edit(req))
        throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
      if (
        !Number.isInteger(entityId) ||
        !(await policy.exists(db, req.auth!.companyId, entityId, req))
      )
        throw new HttpError(404, 'NOT_FOUND', 'Record not found');

      const body = req.body as Buffer;
      if (!Buffer.isBuffer(body) || body.length === 0)
        throw new HttpError(400, 'EMPTY_FILE', 'No file received');
      if (body.length > MAX_ATTACHMENT_BYTES)
        throw new HttpError(413, 'FILE_TOO_LARGE', 'Files can be at most 10 MB');
      const filename = safeFilename(String(req.query.filename ?? 'file'));
      const type = sniff(body);
      const ext = path.extname(filename).toLowerCase();
      if (!type || !type.ext.includes(ext)) {
        throw new HttpError(
          415,
          'UNSUPPORTED_TYPE',
          'Allowed files: PDF, PNG, JPG, WEBP, DOCX, XLSX (the file contents must match its extension)',
        );
      }

      const dir = path.join(root, String(req.auth!.companyId));
      await mkdir(dir, { recursive: true });
      const storedName = `${randomBytes(16).toString('hex')}${ext}`;
      const full = path.join(dir, storedName);
      await writeFile(full, body, { flag: 'wx' });
      try {
        await scan(full);
      } catch (e) {
        await rm(full, { force: true });
        throw e;
      }
      const row = await db.attachment.create({
        data: {
          companyId: req.auth!.companyId,
          entityType,
          entityId,
          filename,
          storedName,
          mimeType: type.mime,
          sizeBytes: body.length,
          sha256: createHash('sha256').update(body).digest('hex'),
          uploadedBy: req.auth!.userId,
        },
      });
      await audit(db, actor(req), {
        action: 'attachment.uploaded',
        entity: entityType,
        entityId,
        after: { attachmentId: row.id, filename, size: body.length },
      });
      res.status(201).json({
        id: row.id,
        filename: row.filename,
        mimeType: row.mimeType,
        sizeBytes: row.sizeBytes,
        createdAt: row.createdAt,
      });
    }),
  );

  router.get(
    '/attachments/:id/download',
    asyncHandler(async (req, res) => {
      const a = await db.attachment.findFirst({
        where: { id: Number(req.params.id), companyId: req.auth!.companyId, deletedAt: null },
      });
      if (!a) throw new HttpError(404, 'NOT_FOUND', 'File not found');
      const policy = policyFor(req, a.entityType);
      if (!policy.view(req))
        throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
      const data = await readFile(path.join(root, String(a.companyId), a.storedName)).catch(() => {
        throw new HttpError(404, 'NOT_FOUND', 'File not found');
      });
      res.setHeader('Content-Type', a.mimeType);
      res.setHeader('X-Content-Type-Options', 'nosniff');
      // Always a download, never rendered inside the app's origin.
      res.setHeader(
        'Content-Disposition',
        `attachment; filename="${a.filename.replace(/"/g, '')}"`,
      );
      res.send(data);
    }),
  );

  router.delete(
    '/attachments/:id',
    asyncHandler(async (req, res) => {
      const a = await db.attachment.findFirst({
        where: { id: Number(req.params.id), companyId: req.auth!.companyId, deletedAt: null },
      });
      if (!a) throw new HttpError(404, 'NOT_FOUND', 'File not found');
      if (!policyFor(req, a.entityType).edit(req))
        throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
      await db.attachment.update({ where: { id: a.id }, data: { deletedAt: new Date() } });
      await audit(db, actor(req), {
        action: 'attachment.deleted',
        entity: a.entityType,
        entityId: a.entityId,
        before: { attachmentId: a.id, filename: a.filename },
      });
      res.status(204).end();
    }),
  );

  return router;
}
